// src/cockpit4/components/AccountPayableReceivable.tsx
// ============================================
// 应收应付管理组件（财务中心 P1-功能7）
//
// 功能规格：
//   1. 应收账款管理：客户应收记录（客户名/金额/开票日/到期日/状态）
//   2. 应付账款管理：供应商应付记录（供应商名/金额/账单日/到期日/状态）
//   3. 账龄分析：0-30天/31-60天/61-90天/90天以上 四档分布柱状图
//   4. 收款提醒：即将到期（7天内）/ 已逾期 的应收自动高亮
//   5. 催款功能：生成催款记录（含催款日期/方式/结果/下次行动）
//   6. 付款计划：付款审批流程（待审批/已批准/已付款）
//   7. 坏账预警：逾期 > 90天 的应收标记为坏账风险
//   8. 现金流影响分析：基于应收应付到期日预测未来30天现金流
//
// 数据来源（禁止假数据）：
//   - 应收应付记录：localStorage（key: xeaos_finance_ar_ap）持久化用户人工录入
//   - 无数据时显示"暂无应收应付记录 / 点击添加"
//
// 权限：财务中心仅超级管理员开放
//   const isSuperAdmin = useUserStore((s) => s.isSuperAdmin())
//   非超管显示权限提示
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeftRight,
  Plus,
  X,
  RefreshCw,
  Loader2,
  Trash2,
  Pencil,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Save,
  Database,
  Lock,
  Calendar,
  AlertOctagon,
  Phone,
  Mail,
  MessageSquare,
  Users,
  CheckCircle,
  Clock,
  XCircle,
  Banknote,
  Wallet,
  Activity,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import { useUserStore } from "../../stores/userStore";

// ============================================
// 类型定义
// ============================================

/** 账款类型：应收 / 应付 */
type AccountType = "receivable" | "payable";

/** 账款状态：待处理 / 已逾期 / 部分收付 / 已结清 / 坏账 */
type AccountStatus =
  | "pending"
  | "overdue"
  | "partial_paid"
  | "paid"
  | "bad_debt";

/** 应收应付记录 */
interface AccountRecord {
  id: string;
  type: AccountType;
  /** 客户名 or 供应商名 */
  counterparty: string;
  /** 总金额 */
  amount: number;
  /** 已收/已付金额 */
  paidAmount: number;
  /** 开票日/账单日 */
  invoiceDate: string;
  /** 到期日 */
  dueDate: string;
  status: AccountStatus;
  note?: string;
  /** 催款记录（仅应收） */
  collectionHistory?: CollectionRecord[];
  /** 付款审批状态（仅应付） */
  approvalStatus?: "pending" | "approved" | "rejected";
  createdAt: string;
  updatedAt: string;
}

/** 催款记录 */
interface CollectionRecord {
  id: string;
  date: string;
  method: "phone" | "email" | "wechat" | "visit";
  contactPerson: string;
  result: string;
  nextAction?: string;
}

/** 账龄分桶 */
interface AgingBucket {
  range: "0-30" | "31-60" | "61-90" | "90+";
  receivableAmount: number;
  payableAmount: number;
  count: number;
}

// ============================================
// 常量配置
// ============================================

const AR_AP_KEY = "xeaos_finance_ar_ap";

/** 状态中文映射 + 颜色 */
const STATUS_META: Record<
  AccountStatus,
  { label: string; color: string; bg: string }
> = {
  pending: {
    label: "待处理",
    color: "#94A3B8",
    bg: "rgba(148,163,184,0.15)",
  },
  overdue: {
    label: "已逾期",
    color: "#F59E0B",
    bg: "rgba(245,158,11,0.15)",
  },
  partial_paid: {
    label: "部分收付",
    color: "#3B82F6",
    bg: "rgba(59,130,246,0.15)",
  },
  paid: {
    label: "已结清",
    color: "#10B981",
    bg: "rgba(16,185,129,0.15)",
  },
  bad_debt: {
    label: "坏账风险",
    color: "#EF4444",
    bg: "rgba(239,68,68,0.15)",
  },
};

/** 催款方式映射 */
const COLLECTION_METHOD_META: Record<
  CollectionRecord["method"],
  { label: string; icon: typeof Phone }
> = {
  phone: { label: "电话", icon: Phone },
  email: { label: "邮件", icon: Mail },
  wechat: { label: "微信", icon: MessageSquare },
  visit: { label: "上门", icon: Users },
};

/** 账龄档位配置 */
const AGING_RANGES: AgingBucket["range"][] = ["0-30", "31-60", "61-90", "90+"];

/** 账龄档位中文标签 */
const AGING_LABEL: Record<AgingBucket["range"], string> = {
  "0-30": "0-30天",
  "31-60": "31-60天",
  "61-90": "61-90天",
  "90+": "90天以上",
};

// ============================================
// localStorage 持久化
// ============================================

/** 加载所有应收应付记录 */
function loadRecords(): AccountRecord[] {
  try {
    const raw = localStorage.getItem(AR_AP_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw) as AccountRecord[];
    if (!Array.isArray(list)) return [];
    return list;
  } catch {
    return [];
  }
}

/** 保存所有应收应付记录 */
function saveRecords(records: AccountRecord[]): void {
  try {
    localStorage.setItem(AR_AP_KEY, JSON.stringify(records));
  } catch {
    // 静默降级
  }
}

// ============================================
// 工具函数
// ============================================

/** 生成唯一 ID */
function genId(): string {
  return `arap-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/** 格式化金额（元 → 万元/元），防御 undefined/NaN */
function formatMoney(amount: number): string {
  const num = Number(amount);
  if (!Number.isFinite(num) || num < 0) return "¥ 0";
  if (num >= 10000) {
    return `¥ ${(num / 10000).toFixed(2)} 万`;
  }
  return `¥ ${num.toLocaleString()}`;
}

/** 格式化金额（带正负号，用于现金流方向） */
function formatSignedMoney(amount: number): string {
  const num = Number(amount);
  if (!Number.isFinite(num)) return "¥ 0";
  const sign = num > 0 ? "+" : num < 0 ? "-" : "";
  const abs = Math.abs(num);
  if (abs >= 10000) {
    return `${sign}¥ ${(abs / 10000).toFixed(2)} 万`;
  }
  return `${sign}¥ ${abs.toLocaleString()}`;
}

/** 格式化日期（YYYY-MM-DD） */
function formatDate(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return iso.slice(0, 10);
}

/** 格式化日期时间 */
function formatDateTime(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return `${iso.slice(0, 10)} ${iso.slice(11, 16)}`;
}

/**
 * 账龄计算：按到期日计算逾期天数归属档位
 * days = now - dueDate；负数（未到期）归入 0-30 档
 */
function calculateAging(dueDate: string): AgingBucket["range"] {
  const days = Math.floor(
    (Date.now() - new Date(dueDate).getTime()) / 86400000
  );
  if (days <= 30) return "0-30";
  if (days <= 60) return "31-60";
  if (days <= 90) return "61-90";
  return "90+";
}

/** 计算逾期天数（正数=已逾期，负数=未到期） */
function overdueDays(dueDate: string): number {
  return Math.floor((Date.now() - new Date(dueDate).getTime()) / 86400000);
}

/** 是否即将到期（7天内到期且未结清） */
function isDueSoon(dueDate: string): boolean {
  const days = Math.floor(
    (new Date(dueDate).getTime() - Date.now()) / 86400000
  );
  return days >= 0 && days <= 7;
}

/** 是否已逾期 */
function isOverdue(dueDate: string): boolean {
  return overdueDays(dueDate) > 0;
}

/**
 * 动态计算账款状态（基于到期日 + 已收付金额）
 * 优先级：已结清 > 坏账(>90天) > 已逾期 > 部分收付 > 待处理
 */
function computeStatus(record: AccountRecord): AccountStatus {
  const outstanding = record.amount - record.paidAmount;
  if (outstanding <= 0 && record.amount > 0) return "paid";
  const days = overdueDays(record.dueDate);
  if (days > 90) return "bad_debt";
  if (days > 0) return "overdue";
  if (record.paidAmount > 0) return "partial_paid";
  return "pending";
}

/** 获取未结清余额 */
function outstandingAmount(record: AccountRecord): number {
  return Math.max(0, record.amount - record.paidAmount);
}

// ============================================
// 主组件
// ============================================

export default function AccountPayableReceivable() {
  // 权限控制：财务中心仅超级管理员开放
  const isSuperAdmin = useUserStore((s) => s.isSuperAdmin());
  // 当前登录用户（用于操作记录）
  const currentUser = useUserStore((s) => s.currentUser);

  const [records, setRecords] = useState<AccountRecord[]>([]);
  const [loading, setLoading] = useState(true);

  // Tab 切换：应收 / 应付
  const [activeTab, setActiveTab] = useState<AccountType>("receivable");

  // 弹窗状态
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState<AccountRecord | null>(null);
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const [collectionTarget, setCollectionTarget] = useState<AccountRecord | null>(
    null
  );
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentTarget, setPaymentTarget] = useState<AccountRecord | null>(null);

  // ============================================
  // 加载数据
  // ============================================
  const loadData = useCallback(() => {
    setLoading(true);
    setRecords(loadRecords());
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ============================================
  // 派生数据：当前 Tab 的记录
  // ============================================
  const tabRecords = useMemo(
    () => records.filter((r) => r.type === activeTab),
    [records, activeTab]
  );

  // ============================================
  // 摘要统计（全局，应收应付净额等）
  // ============================================
  const summary = useMemo(() => {
    let totalReceivable = 0;
    let totalPayable = 0;
    let paidReceivable = 0;
    let paidPayable = 0;
    let overdueAmount = 0;
    let badDebtAmount = 0;

    for (const r of records) {
      const outstanding = outstandingAmount(r);
      const status = computeStatus(r);
      if (r.type === "receivable") {
        totalReceivable += r.amount;
        paidReceivable += r.paidAmount;
        if (status === "bad_debt") badDebtAmount += outstanding;
        if (status === "overdue" || status === "bad_debt")
          overdueAmount += outstanding;
      } else {
        totalPayable += r.amount;
        paidPayable += r.paidAmount;
        if (status === "overdue" || status === "bad_debt")
          overdueAmount += outstanding;
      }
    }

    const netReceivable = totalReceivable - paidReceivable;
    const netPayable = totalPayable - paidPayable;
    const net = netReceivable - netPayable;

    return {
      totalReceivable,
      totalPayable,
      netReceivable,
      netPayable,
      net,
      overdueAmount,
      badDebtAmount,
    };
  }, [records]);

  // ============================================
  // 账龄分布（4档，应收/应付分别统计未结清金额）
  // ============================================
  const agingData = useMemo(() => {
    const buckets: Record<AgingBucket["range"], AgingBucket> = {
      "0-30": { range: "0-30", receivableAmount: 0, payableAmount: 0, count: 0 },
      "31-60": { range: "31-60", receivableAmount: 0, payableAmount: 0, count: 0 },
      "61-90": { range: "61-90", receivableAmount: 0, payableAmount: 0, count: 0 },
      "90+": { range: "90+", receivableAmount: 0, payableAmount: 0, count: 0 },
    };

    for (const r of records) {
      // 已结清的不计入账龄
      if (outstandingAmount(r) <= 0) continue;
      const bucket = buckets[calculateAging(r.dueDate)];
      const outstanding = outstandingAmount(r);
      if (r.type === "receivable") {
        bucket.receivableAmount += outstanding;
      } else {
        bucket.payableAmount += outstanding;
      }
      bucket.count += 1;
    }

    // 保留两位小数
    return AGING_RANGES.map((range) => {
      const b = buckets[range];
      return {
        ...b,
        receivableAmount: Math.round(b.receivableAmount * 100) / 100,
        payableAmount: Math.round(b.payableAmount * 100) / 100,
      };
    });
  }, [records]);

  // ============================================
  // 现金流预测：未来30天到期应收/应付
  // ============================================
  const cashFlowForecast = useMemo(() => {
    const now = Date.now();
    const horizon = now + 30 * 86400000;
    const events: Array<{
      date: string;
      type: "inflow" | "outflow";
      counterparty: string;
      amount: number;
      recordType: AccountType;
    }> = [];

    let totalInflow = 0;
    let totalOutflow = 0;

    for (const r of records) {
      const outstanding = outstandingAmount(r);
      if (outstanding <= 0) continue;
      const dueTime = new Date(r.dueDate).getTime();
      // 仅统计未来30天内到期（含今天）
      if (dueTime < now || dueTime > horizon) continue;
      if (r.type === "receivable") {
        events.push({
          date: r.dueDate,
          type: "inflow",
          counterparty: r.counterparty,
          amount: outstanding,
          recordType: "receivable",
        });
        totalInflow += outstanding;
      } else {
        events.push({
          date: r.dueDate,
          type: "outflow",
          counterparty: r.counterparty,
          amount: outstanding,
          recordType: "payable",
        });
        totalOutflow += outstanding;
      }
    }

    events.sort((a, b) => a.date.localeCompare(b.date));

    return {
      events,
      totalInflow: Math.round(totalInflow * 100) / 100,
      totalOutflow: Math.round(totalOutflow * 100) / 100,
      net: Math.round((totalInflow - totalOutflow) * 100) / 100,
    };
  }, [records]);

  // ============================================
  // CRUD：保存记录（创建或更新）
  // ============================================
  const handleSaveRecord = useCallback(
    (data: Omit<AccountRecord, "id" | "createdAt" | "updatedAt"> & { id?: string }) => {
      const nowIso = new Date().toISOString();
      if (data.id) {
        // 更新
        const next = records.map((r) => {
          if (r.id !== data.id) return r;
          const updated: AccountRecord = {
            ...r,
            type: data.type,
            counterparty: data.counterparty,
            amount: data.amount,
            paidAmount: data.paidAmount,
            invoiceDate: data.invoiceDate,
            dueDate: data.dueDate,
            note: data.note,
            approvalStatus: data.approvalStatus,
            status: computeStatus({ ...r, ...data } as AccountRecord),
            updatedAt: nowIso,
          };
          return updated;
        });
        setRecords(next);
        saveRecords(next);
      } else {
        // 新建
        const newRecord: AccountRecord = {
          id: genId(),
          type: data.type,
          counterparty: data.counterparty,
          amount: data.amount,
          paidAmount: data.paidAmount,
          invoiceDate: data.invoiceDate,
          dueDate: data.dueDate,
          note: data.note,
          approvalStatus: data.approvalStatus,
          collectionHistory: data.type === "receivable" ? [] : undefined,
          status: computeStatus({
            type: data.type,
            counterparty: data.counterparty,
            amount: data.amount,
            paidAmount: data.paidAmount,
            invoiceDate: data.invoiceDate,
            dueDate: data.dueDate,
          } as AccountRecord),
          createdAt: nowIso,
          updatedAt: nowIso,
        };
        const next = [newRecord, ...records];
        setRecords(next);
        saveRecords(next);
      }
      setShowEditModal(false);
      setEditingRecord(null);
    },
    [records]
  );

  /** 删除记录 */
  const handleDeleteRecord = useCallback(
    (id: string) => {
      const next = records.filter((r) => r.id !== id);
      setRecords(next);
      saveRecords(next);
    },
    [records]
  );

  /** 添加催款记录（仅应收） */
  const handleAddCollection = useCallback(
    (recordId: string, collection: Omit<CollectionRecord, "id">) => {
      const nowIso = new Date().toISOString();
      const next = records.map((r) => {
        if (r.id !== recordId) return r;
        const newCollection: CollectionRecord = {
          ...collection,
          id: genId(),
        };
        return {
          ...r,
          collectionHistory: [...(r.collectionHistory || []), newCollection],
          updatedAt: nowIso,
        };
      });
      setRecords(next);
      saveRecords(next);
      setShowCollectionModal(false);
      setCollectionTarget(null);
    },
    [records]
  );

  /** 更新付款审批状态（仅应付） */
  const handleUpdateApproval = useCallback(
    (recordId: string, approvalStatus: "approved" | "rejected") => {
      const nowIso = new Date().toISOString();
      const next = records.map((r) =>
        r.id === recordId
          ? { ...r, approvalStatus, updatedAt: nowIso }
          : r
      );
      setRecords(next);
      saveRecords(next);
    },
    [records]
  );

  /** 记录收/付款（更新 paidAmount） */
  const handleRecordPayment = useCallback(
    (recordId: string, paidAmount: number) => {
      const nowIso = new Date().toISOString();
      const next = records.map((r) => {
        if (r.id !== recordId) return r;
        const clamped = Math.max(0, Math.min(r.amount, paidAmount));
        const updated = { ...r, paidAmount: clamped };
        return {
          ...updated,
          status: computeStatus(updated),
          updatedAt: nowIso,
        };
      });
      setRecords(next);
      saveRecords(next);
      setShowPaymentModal(false);
      setPaymentTarget(null);
    },
    [records]
  );

  // ============================================
  // 渲染：权限提示（非超管）
  // ============================================
  if (!isSuperAdmin) {
    return (
      <div className="h-full w-full overflow-hidden p-4">
        <div className="h-full flex flex-col items-center justify-center rounded-xl border border-red-500/20 bg-red-500/[0.02] backdrop-blur-xl">
          <div className="w-16 h-16 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center mb-4">
            <Lock size={28} className="text-red-400" />
          </div>
          <h2 className="text-[16px] font-semibold text-white mb-2">权限受限</h2>
          <p className="text-[12px] font-mono text-gray-400 text-center max-w-sm leading-relaxed">
            应收应付管理仅对超级管理员开放。
            <br />
            普通员工无权查看财务账款数据。
          </p>
          <p className="text-[10px] font-mono text-gray-600 mt-4">
            如需访问，请联系超级管理员授权。
          </p>
        </div>
      </div>
    );
  }

  // ============================================
  // 主渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative h-full flex flex-col rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-cyan-500/15 border border-cyan-500/30">
            <ArrowLeftRight size={13} className="text-cyan-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">
              应收应付管理
            </h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              AR / AP · 账龄分析 · 催款 · 付款审批 · 现金流预测
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* 刷新 */}
          <button
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.08] transition-colors disabled:opacity-50"
            title="刷新数据"
          >
            {loading ? (
              <Loader2 size={11} className="text-gray-400 animate-spin" />
            ) : (
              <RefreshCw size={11} className="text-gray-400" />
            )}
          </button>
        </div>
      </div>

      {/* Tab 切换 + 添加按钮 */}
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-white/[0.04] bg-white/[0.01]">
        <div className="flex items-center rounded-md border border-white/[0.06] overflow-hidden">
          <button
            onClick={() => setActiveTab("receivable")}
            className={`px-3 py-1 text-[9px] font-mono uppercase tracking-wider transition-colors ${
              activeTab === "receivable"
                ? "bg-cyan-500/20 text-cyan-300"
                : "bg-white/[0.02] text-gray-500 hover:bg-white/[0.04]"
            }`}
          >
            应收账款
          </button>
          <button
            onClick={() => setActiveTab("payable")}
            className={`px-3 py-1 text-[9px] font-mono uppercase tracking-wider transition-colors ${
              activeTab === "payable"
                ? "bg-cyan-500/20 text-cyan-300"
                : "bg-white/[0.02] text-gray-500 hover:bg-white/[0.04]"
            }`}
          >
            应付账款
          </button>
        </div>

        <div className="flex-1" />

        <button
          onClick={() => {
            setEditingRecord(null);
            setShowEditModal(true);
          }}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-cyan-500/15 border border-cyan-500/30 hover:bg-cyan-500/25 transition-colors"
        >
          <Plus size={11} className="text-cyan-400" />
          <span className="text-[9px] font-mono text-cyan-300 uppercase tracking-wider">
            添加{activeTab === "receivable" ? "应收" : "应付"}
          </span>
        </button>
      </div>

      {/* 内容区 */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 size={20} className="animate-spin text-cyan-400" />
            <span className="ml-2 text-[11px] font-mono text-gray-500">
              加载应收应付数据...
            </span>
          </div>
        ) : records.length === 0 ? (
          /* 空状态 */
          <EmptyState
            onCreate={() => {
              setEditingRecord(null);
              setShowEditModal(true);
            }}
          />
        ) : (
          <>
            {/* 摘要 KPI 行 */}
            <div className="grid grid-cols-5 gap-2">
              <KpiCell
                label="应收总额"
                value={formatMoney(summary.totalReceivable)}
                color="#06B6D4"
                icon={TrendingUp}
              />
              <KpiCell
                label="应付总额"
                value={formatMoney(summary.totalPayable)}
                color="#A78BFA"
                icon={TrendingDown}
              />
              <KpiCell
                label="净额（应收-应付）"
                value={formatSignedMoney(summary.net)}
                color={summary.net >= 0 ? "#10B981" : "#EF4444"}
                icon={Wallet}
              />
              <KpiCell
                label="逾期金额"
                value={formatMoney(summary.overdueAmount)}
                color="#F59E0B"
                icon={AlertTriangle}
              />
              <KpiCell
                label="坏账风险金额"
                value={formatMoney(summary.badDebtAmount)}
                color="#EF4444"
                icon={AlertOctagon}
              />
            </div>

            {/* 坏账预警条 */}
            {summary.badDebtAmount > 0 && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-red-500/30 bg-red-500/[0.06]">
                <AlertOctagon size={12} className="text-red-400" />
                <span className="text-[10px] font-mono text-red-300 uppercase tracking-wider">
                  坏账预警：检测到逾期 &gt; 90天的应收账款 · 风险金额 {formatMoney(summary.badDebtAmount)}
                </span>
              </div>
            )}

            {/* 主体：左侧账龄图 + 右侧记录列表 */}
            <div className="grid grid-cols-3 gap-3">
              {/* 左侧：账龄分布柱状图 */}
              <div className="col-span-1 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
                <div className="flex items-center gap-1.5 mb-2">
                  <Activity size={11} className="text-cyan-400" />
                  <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                    账龄分布（未结清金额）
                  </span>
                </div>
                <div className="h-[240px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={agingData} barGap={2}>
                      <CartesianGrid stroke="#ffffff10" strokeDasharray="3 3" />
                      <XAxis
                        dataKey="range"
                        tick={{
                          fill: "#9CA3AF",
                          fontSize: 10,
                          fontFamily: "monospace",
                        }}
                        axisLine={{ stroke: "#ffffff20" }}
                      />
                      <YAxis
                        tick={{
                          fill: "#9CA3AF",
                          fontSize: 10,
                          fontFamily: "monospace",
                        }}
                        axisLine={{ stroke: "#ffffff20" }}
                      />
                      <Tooltip
                        contentStyle={{
                          background: "#0F1623",
                          border: "1px solid #ffffff20",
                          borderRadius: 6,
                          fontSize: 11,
                          fontFamily: "monospace",
                        }}
                        labelStyle={{ color: "#E5E7EB" }}
                        formatter={(value: any) => formatMoney(Number(value))}
                      />
                      <Legend
                        wrapperStyle={{
                          fontSize: 10,
                          fontFamily: "monospace",
                        }}
                      />
                      <Bar dataKey="receivableAmount" name="应收" fill="#06B6D4" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="payableAmount" name="应付" fill="#A78BFA" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                {/* 账龄档位明细 */}
                <div className="mt-2 space-y-1">
                  {agingData.map((b) => (
                    <div
                      key={b.range}
                      className="flex items-center justify-between px-2 py-1 rounded border border-white/[0.04] bg-white/[0.01]"
                    >
                      <span className="text-[9px] font-mono text-gray-400">
                        {AGING_LABEL[b.range]}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] font-mono text-cyan-400">
                          应收 {formatMoney(b.receivableAmount)}
                        </span>
                        <span className="text-[9px] font-mono text-purple-400">
                          应付 {formatMoney(b.payableAmount)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 右侧：记录列表 */}
              <div className="col-span-2 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5">
                    <Database size={11} className="text-cyan-400" />
                    <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                      {activeTab === "receivable" ? "应收账款记录" : "应付账款记录"}（共 {tabRecords.length} 条）
                    </span>
                  </div>
                </div>

                {tabRecords.length === 0 ? (
                  <div className="text-center py-8 text-[10px] font-mono text-gray-600">
                    暂无{activeTab === "receivable" ? "应收" : "应付"}记录 · 点击右上方"添加"录入
                  </div>
                ) : (
                  <div className="space-y-1.5 max-h-[420px] overflow-y-auto">
                    {tabRecords.map((record) => (
                      <RecordRow
                        key={record.id}
                        record={record}
                        onEdit={() => {
                          setEditingRecord(record);
                          setShowEditModal(true);
                        }}
                        onDelete={() => handleDeleteRecord(record.id)}
                        onCollection={() => {
                          setCollectionTarget(record);
                          setShowCollectionModal(true);
                        }}
                        onApprove={() => handleUpdateApproval(record.id, "approved")}
                        onReject={() => handleUpdateApproval(record.id, "rejected")}
                        onPayment={() => {
                          setPaymentTarget(record);
                          setShowPaymentModal(true);
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* 底部：现金流预测 */}
            <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
              <div className="flex items-center gap-1.5 mb-2">
                <Calendar size={11} className="text-cyan-400" />
                <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                  现金流预测 · 未来30天到期
                </span>
              </div>

              {/* 现金流汇总 */}
              <div className="grid grid-cols-3 gap-2 mb-2">
                <div className="px-2.5 py-1.5 rounded-md border border-cyan-500/20 bg-cyan-500/[0.04]">
                  <div className="text-[8px] font-mono text-gray-500 uppercase">
                    预计流入（应收到期）
                  </div>
                  <div className="text-[12px] font-mono font-semibold text-cyan-300">
                    {formatMoney(cashFlowForecast.totalInflow)}
                  </div>
                </div>
                <div className="px-2.5 py-1.5 rounded-md border border-purple-500/20 bg-purple-500/[0.04]">
                  <div className="text-[8px] font-mono text-gray-500 uppercase">
                    预计流出（应付到期）
                  </div>
                  <div className="text-[12px] font-mono font-semibold text-purple-300">
                    {formatMoney(cashFlowForecast.totalOutflow)}
                  </div>
                </div>
                <div className="px-2.5 py-1.5 rounded-md border border-white/[0.06] bg-white/[0.02]">
                  <div className="text-[8px] font-mono text-gray-500 uppercase">
                    净现金流
                  </div>
                  <div
                    className={`text-[12px] font-mono font-semibold ${
                      cashFlowForecast.net >= 0 ? "text-green-400" : "text-red-400"
                    }`}
                  >
                    {formatSignedMoney(cashFlowForecast.net)}
                  </div>
                </div>
              </div>

              {/* 现金流事件列表 */}
              {cashFlowForecast.events.length === 0 ? (
                <div className="text-center py-4 text-[10px] font-mono text-gray-600">
                  未来30天暂无到期账款
                </div>
              ) : (
                <div className="space-y-1 max-h-[180px] overflow-y-auto">
                  {cashFlowForecast.events.map((ev, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 px-2.5 py-1.5 rounded border border-white/[0.04] bg-white/[0.01]"
                    >
                      <span className="text-[9px] font-mono text-gray-500 w-[80px] flex-shrink-0">
                        {formatDate(ev.date)}
                      </span>
                      <span
                        className={`px-1.5 py-0.5 rounded text-[8px] font-mono uppercase ${
                          ev.type === "inflow"
                            ? "bg-cyan-500/15 text-cyan-300"
                            : "bg-purple-500/15 text-purple-300"
                        }`}
                      >
                        {ev.type === "inflow" ? "流入" : "流出"}
                      </span>
                      <span className="text-[10px] text-gray-300 flex-1 truncate">
                        {ev.counterparty}
                      </span>
                      <span
                        className={`text-[10px] font-mono flex-shrink-0 ${
                          ev.type === "inflow" ? "text-cyan-300" : "text-purple-300"
                        }`}
                      >
                        {ev.type === "inflow" ? "+" : "-"}
                        {formatMoney(ev.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 数据来源说明 */}
            <div className="px-3 py-2 rounded-md border border-blue-500/15 bg-blue-500/[0.02] text-[10px] font-mono text-blue-300/70">
              <Database size={11} className="inline mr-1" />
              数据来源：localStorage(xeaos_finance_ar_ap) 人工录入 · 共 {records.length} 条记录
              <span className="ml-2 text-gray-500">
                · 应收 {records.filter((r) => r.type === "receivable").length} 条 · 应付 {records.filter((r) => r.type === "payable").length} 条
              </span>
            </div>
          </>
        )}
      </div>

      {/* 编辑/创建弹窗 */}
      <AnimatePresence>
        {showEditModal && (
          <RecordEditModal
            record={editingRecord}
            defaultType={activeTab}
            onSubmit={handleSaveRecord}
            onClose={() => {
              setShowEditModal(false);
              setEditingRecord(null);
            }}
          />
        )}
      </AnimatePresence>

      {/* 催款记录弹窗 */}
      <AnimatePresence>
        {showCollectionModal && collectionTarget && (
          <CollectionModal
            record={collectionTarget}
            onSubmit={(collection) =>
              handleAddCollection(collectionTarget.id, collection)
            }
            onClose={() => {
              setShowCollectionModal(false);
              setCollectionTarget(null);
            }}
          />
        )}
      </AnimatePresence>

      {/* 收/付款弹窗 */}
      <AnimatePresence>
        {showPaymentModal && paymentTarget && (
          <PaymentModal
            record={paymentTarget}
            onSubmit={(paid) => handleRecordPayment(paymentTarget.id, paid)}
            onClose={() => {
              setShowPaymentModal(false);
              setPaymentTarget(null);
            }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============================================
// KPI 单元格
// ============================================
function KpiCell({
  label,
  value,
  color,
  icon: Icon,
}: {
  label: string;
  value: string;
  color: string;
  icon: typeof Wallet;
}) {
  return (
    <div className="rounded-lg border border-white/[0.04] bg-white/[0.01] px-3 py-2">
      <div className="flex items-center gap-1 mb-0.5">
        <Icon size={9} style={{ color }} />
        <span className="text-[8px] font-mono text-gray-500 uppercase tracking-wider">
          {label}
        </span>
      </div>
      <div
        className="text-[14px] font-mono font-semibold"
        style={{ color }}
      >
        {value}
      </div>
    </div>
  );
}

// ============================================
// 空状态
// ============================================
function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-14 h-14 rounded-full bg-white/[0.03] border border-white/[0.06] flex items-center justify-center mb-3">
        <ArrowLeftRight size={24} className="text-gray-600" />
      </div>
      <p className="text-[12px] text-gray-400 mb-1">暂无应收应付记录</p>
      <p className="text-[10px] font-mono text-gray-600 mb-4">
        点击添加，开始录入客户应收 / 供应商应付
      </p>
      <button
        onClick={onCreate}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-cyan-500/15 border border-cyan-500/30 hover:bg-cyan-500/25 transition-colors"
      >
        <Plus size={12} className="text-cyan-400" />
        <span className="text-[10px] font-mono text-cyan-300 uppercase tracking-wider">
          添加记录
        </span>
      </button>
    </div>
  );
}

// ============================================
// 记录行（表格行：状态/账龄/催款提醒/操作）
// ============================================
function RecordRow({
  record,
  onEdit,
  onDelete,
  onCollection,
  onApprove,
  onReject,
  onPayment,
}: {
  record: AccountRecord;
  onEdit: () => void;
  onDelete: () => void;
  onCollection: () => void;
  onApprove: () => void;
  onReject: () => void;
  onPayment: () => void;
}) {
  const status = computeStatus(record);
  const statusMeta = STATUS_META[status];
  const outstanding = outstandingAmount(record);
  const dueSoon = isDueSoon(record.dueDate);
  const overdue = isOverdue(record.dueDate);
  const aging = calculateAging(record.dueDate);
  const isBadDebt = status === "bad_debt";

  // 应收催款提醒高亮：即将到期（7天内）或已逾期
  const highlight =
    record.type === "receivable" && outstanding > 0 && (dueSoon || overdue);

  // 应付审批状态
  const approvalStatus = record.approvalStatus || "pending";

  return (
    <div
      className={`rounded-md border p-2.5 transition-colors ${
        isBadDebt
          ? "border-red-500/30 bg-red-500/[0.04] ring-1 ring-red-500/15"
          : highlight
          ? "border-amber-500/30 bg-amber-500/[0.04]"
          : "border-white/[0.04] bg-white/[0.02]"
      }`}
    >
      <div className="flex items-center gap-2.5">
        {/* 状态色标 */}
        <div
          className="w-1 h-10 rounded-full flex-shrink-0"
          style={{ background: statusMeta.color }}
        />

        {/* 主体信息 */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <span className="text-[11px] text-gray-200 font-medium truncate">
              {record.counterparty}
            </span>
            {/* 状态标签 */}
            <span
              className="px-1.5 py-0.5 rounded text-[8px] font-mono uppercase flex-shrink-0"
              style={{ background: statusMeta.bg, color: statusMeta.color }}
            >
              {statusMeta.label}
            </span>
            {/* 账龄标签 */}
            <span className="px-1 py-0.5 rounded text-[8px] font-mono bg-white/[0.04] text-gray-400 flex-shrink-0">
              {AGING_LABEL[aging]}
            </span>
            {/* 催款提醒标签 */}
            {highlight && !isBadDebt && (
              <span className="flex items-center gap-0.5 px-1 py-0.5 rounded text-[8px] font-mono uppercase bg-amber-500/15 text-amber-400 flex-shrink-0">
                <AlertTriangle size={7} />
                {overdue ? "已逾期" : "即将到期"}
              </span>
            )}
            {/* 坏账标签 */}
            {isBadDebt && (
              <span className="flex items-center gap-0.5 px-1 py-0.5 rounded text-[8px] font-mono uppercase bg-red-500/15 text-red-400 flex-shrink-0">
                <AlertOctagon size={7} />
                坏账风险
              </span>
            )}
            {/* 应付审批状态 */}
            {record.type === "payable" && (
              <span
                className={`px-1 py-0.5 rounded text-[8px] font-mono uppercase flex-shrink-0 ${
                  approvalStatus === "approved"
                    ? "bg-green-500/15 text-green-400"
                    : approvalStatus === "rejected"
                    ? "bg-red-500/15 text-red-400"
                    : "bg-gray-500/15 text-gray-400"
                }`}
              >
                {approvalStatus === "approved"
                  ? "已批准"
                  : approvalStatus === "rejected"
                  ? "已拒绝"
                  : "待审批"}
              </span>
            )}
          </div>

          {/* 日期 + 金额 */}
          <div className="flex items-center gap-3 text-[9px] font-mono text-gray-500">
            <span className="flex items-center gap-0.5">
              <Calendar size={8} />
              {record.type === "receivable" ? "开票" : "账单"} {formatDate(record.invoiceDate)}
            </span>
            <span>→</span>
            <span className={overdue ? "text-amber-400" : ""}>
              到期 {formatDate(record.dueDate)}
            </span>
            <span className="text-gray-600">|</span>
            <span className="text-gray-400">
              总额 <span className="text-gray-200">{formatMoney(record.amount)}</span>
            </span>
            {record.paidAmount > 0 && (
              <span className="text-gray-400">
                已{record.type === "receivable" ? "收" : "付"}{" "}
                <span className="text-green-400">{formatMoney(record.paidAmount)}</span>
              </span>
            )}
            {outstanding > 0 && (
              <span className="text-gray-400">
                未结 <span className="text-amber-400">{formatMoney(outstanding)}</span>
              </span>
            )}
          </div>

          {/* 备注 */}
          {record.note && (
            <div className="text-[9px] text-gray-600 mt-0.5 truncate">
              备注：{record.note}
            </div>
          )}

          {/* 催款历史（应收） */}
          {record.type === "receivable" &&
            record.collectionHistory &&
            record.collectionHistory.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {record.collectionHistory
                  .slice()
                  .reverse()
                  .slice(0, 3)
                  .map((c) => {
                    const methodMeta = COLLECTION_METHOD_META[c.method];
                    const MethodIcon = methodMeta.icon;
                    return (
                      <span
                        key={c.id}
                        className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[8px] font-mono bg-white/[0.04] text-gray-400"
                        title={`${formatDateTime(c.date)} · ${c.contactPerson}：${c.result}`}
                      >
                        <MethodIcon size={7} />
                        {formatDate(c.date)}
                      </span>
                    );
                  })}
                {record.collectionHistory.length > 3 && (
                  <span className="text-[8px] font-mono text-gray-600">
                    +{record.collectionHistory.length - 3}
                  </span>
                )}
              </div>
            )}
        </div>

        {/* 操作按钮 */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* 应收：催款 + 收款 */}
          {record.type === "receivable" && outstanding > 0 && (
            <>
              <button
                onClick={onCollection}
                className="p-1 rounded hover:bg-amber-500/15 text-gray-500 hover:text-amber-400 transition-colors"
                title="添加催款记录"
              >
                <Phone size={10} />
              </button>
              <button
                onClick={onPayment}
                className="p-1 rounded hover:bg-green-500/15 text-gray-500 hover:text-green-400 transition-colors"
                title="记录收款"
              >
                <Banknote size={10} />
              </button>
            </>
          )}

          {/* 应付：审批 + 付款 */}
          {record.type === "payable" && outstanding > 0 && (
            <>
              {approvalStatus === "pending" && (
                <>
                  <button
                    onClick={onApprove}
                    className="p-1 rounded hover:bg-green-500/15 text-gray-500 hover:text-green-400 transition-colors"
                    title="批准付款"
                  >
                    <CheckCircle size={10} />
                  </button>
                  <button
                    onClick={onReject}
                    className="p-1 rounded hover:bg-red-500/15 text-gray-500 hover:text-red-400 transition-colors"
                    title="拒绝付款"
                  >
                    <XCircle size={10} />
                  </button>
                </>
              )}
              {approvalStatus === "approved" && (
                <button
                  onClick={onPayment}
                  className="p-1 rounded hover:bg-green-500/15 text-gray-500 hover:text-green-400 transition-colors"
                  title="记录付款"
                >
                  <Banknote size={10} />
                </button>
              )}
            </>
          )}

          {/* 编辑 */}
          <button
            onClick={onEdit}
            className="p-1 rounded hover:bg-white/[0.06] text-gray-500 hover:text-gray-300 transition-colors"
            title="编辑"
          >
            <Pencil size={10} />
          </button>
          {/* 删除 */}
          <button
            onClick={onDelete}
            className="p-1 rounded hover:bg-red-500/15 text-gray-500 hover:text-red-400 transition-colors"
            title="删除"
          >
            <Trash2 size={10} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// 记录编辑/创建弹窗
// ============================================
function RecordEditModal({
  record,
  defaultType,
  onSubmit,
  onClose,
}: {
  record: AccountRecord | null;
  defaultType: AccountType;
  onSubmit: (
    data: Omit<AccountRecord, "id" | "createdAt" | "updatedAt"> & { id?: string }
  ) => void;
  onClose: () => void;
}) {
  const [type, setType] = useState<AccountType>(record?.type || defaultType);
  const [counterparty, setCounterparty] = useState(record?.counterparty || "");
  const [amount, setAmount] = useState<number>(record?.amount || 0);
  const [paidAmount, setPaidAmount] = useState<number>(record?.paidAmount || 0);
  const [invoiceDate, setInvoiceDate] = useState(
    record?.invoiceDate || new Date().toISOString().slice(0, 10)
  );
  const [dueDate, setDueDate] = useState(
    record?.dueDate || new Date().toISOString().slice(0, 10)
  );
  const [note, setNote] = useState(record?.note || "");
  const [approvalStatus, setApprovalStatus] = useState<
    "pending" | "approved" | "rejected"
  >(record?.approvalStatus || "pending");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = () => {
    if (!counterparty.trim()) {
      setError("请输入客户/供应商名称");
      return;
    }
    if (amount <= 0) {
      setError("金额必须大于 0");
      return;
    }
    if (paidAmount < 0 || paidAmount > amount) {
      setError("已收/付金额不能为负且不能超过总金额");
      return;
    }
    if (!invoiceDate || !dueDate) {
      setError("请选择开票/账单日和到期日");
      return;
    }

    onSubmit({
      id: record?.id,
      type,
      counterparty: counterparty.trim(),
      amount: Math.round(amount * 100) / 100,
      paidAmount: Math.round(paidAmount * 100) / 100,
      invoiceDate,
      dueDate,
      status: record?.status || "pending" as AccountStatus,
      note: note.trim() || undefined,
      approvalStatus: type === "payable" ? approvalStatus : undefined,
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg max-h-[85vh] flex flex-col rounded-xl border border-cyan-500/30 bg-[#0F1623] overflow-hidden shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center">
              {record ? (
                <Pencil size={13} className="text-cyan-400" />
              ) : (
                <Plus size={13} className="text-cyan-400" />
              )}
            </div>
            <div>
              <h3 className="text-[13px] font-semibold text-white">
                {record ? "编辑记录" : "添加记录"}
              </h3>
              <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                应收应付管理 · 人工录入
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-white/[0.06] text-gray-400 hover:text-white transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        {/* 表单 */}
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {error && (
            <div className="px-3 py-2 rounded border border-red-500/30 bg-red-500/10 text-[10px] text-red-300 font-mono">
              {error}
            </div>
          )}

          {/* 类型选择（创建时可切换，编辑时锁定） */}
          <div>
            <label className="block text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1.5">
              账款类型
            </label>
            <div className="flex items-center rounded-md border border-white/[0.06] overflow-hidden">
              <button
                onClick={() => !record && setType("receivable")}
                disabled={!!record}
                className={`flex-1 px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider transition-colors disabled:opacity-50 ${
                  type === "receivable"
                    ? "bg-cyan-500/20 text-cyan-300"
                    : "bg-white/[0.02] text-gray-500 hover:bg-white/[0.04]"
                }`}
              >
                应收账款（客户）
              </button>
              <button
                onClick={() => !record && setType("payable")}
                disabled={!!record}
                className={`flex-1 px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider transition-colors disabled:opacity-50 ${
                  type === "payable"
                    ? "bg-cyan-500/20 text-cyan-300"
                    : "bg-white/[0.02] text-gray-500 hover:bg-white/[0.04]"
                }`}
              >
                应付账款（供应商）
              </button>
            </div>
          </div>

          {/* 客户/供应商名称 */}
          <div>
            <label className="block text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1.5">
              {type === "receivable" ? "客户名称" : "供应商名称"}
            </label>
            <input
              type="text"
              value={counterparty}
              onChange={(e) => setCounterparty(e.target.value)}
              placeholder={type === "receivable" ? "请输入客户名称" : "请输入供应商名称"}
              className="w-full px-3 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 placeholder-gray-600 outline-none focus:border-cyan-500/30"
            />
          </div>

          {/* 金额 + 已收付金额 */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1.5">
                总金额（元）
              </label>
              <div className="flex items-center gap-1">
                <span className="text-[10px] font-mono text-gray-600">¥</span>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={amount || ""}
                  onChange={(e) => setAmount(Math.max(0, Number(e.target.value) || 0))}
                  placeholder="0"
                  className="w-full px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 text-right font-mono placeholder-gray-600 outline-none focus:border-cyan-500/30"
                />
              </div>
            </div>
            <div>
              <label className="block text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1.5">
                已{type === "receivable" ? "收" : "付"}金额（元）
              </label>
              <div className="flex items-center gap-1">
                <span className="text-[10px] font-mono text-gray-600">¥</span>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={paidAmount || ""}
                  onChange={(e) =>
                    setPaidAmount(Math.max(0, Number(e.target.value) || 0))
                  }
                  placeholder="0"
                  className="w-full px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 text-right font-mono placeholder-gray-600 outline-none focus:border-cyan-500/30"
                />
              </div>
            </div>
          </div>

          {/* 日期 */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1.5">
                {type === "receivable" ? "开票日" : "账单日"}
              </label>
              <input
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
                className="w-full px-3 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono outline-none focus:border-cyan-500/30"
              />
            </div>
            <div>
              <label className="block text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1.5">
                到期日
              </label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full px-3 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono outline-none focus:border-cyan-500/30"
              />
            </div>
          </div>

          {/* 应付：审批状态 */}
          {type === "payable" && (
            <div>
              <label className="block text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1.5">
                付款审批状态
              </label>
              <div className="flex items-center rounded-md border border-white/[0.06] overflow-hidden">
                {(
                  [
                    { key: "pending", label: "待审批", icon: Clock },
                    { key: "approved", label: "已批准", icon: CheckCircle },
                    { key: "rejected", label: "已拒绝", icon: XCircle },
                  ] as const
                ).map((opt) => {
                  const OptIcon = opt.icon;
                  return (
                    <button
                      key={opt.key}
                      onClick={() => setApprovalStatus(opt.key)}
                      className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-[10px] font-mono uppercase tracking-wider transition-colors ${
                        approvalStatus === opt.key
                          ? "bg-cyan-500/20 text-cyan-300"
                          : "bg-white/[0.02] text-gray-500 hover:bg-white/[0.04]"
                      }`}
                    >
                      <OptIcon size={9} />
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* 备注 */}
          <div>
            <label className="block text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1.5">
              备注（可选）
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="合同编号、付款条件、其他说明..."
              rows={2}
              className="w-full px-3 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 placeholder-gray-600 outline-none focus:border-cyan-500/30 resize-none"
            />
          </div>

          {/* 汇总 */}
          <div className="flex items-center justify-between px-3 py-2 rounded-md border border-cyan-500/20 bg-cyan-500/[0.04]">
            <span className="text-[10px] font-mono text-cyan-300 uppercase tracking-wider">
              未结清余额
            </span>
            <span className="text-[13px] font-mono font-semibold text-cyan-300">
              {formatMoney(Math.max(0, amount - paidAmount))}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-white/[0.06]">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-md text-[10px] font-mono text-gray-400 hover:text-white hover:bg-white/[0.04] transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-cyan-500/20 border border-cyan-500/40 hover:bg-cyan-500/30 transition-colors"
          >
            <Save size={11} className="text-cyan-400" />
            <span className="text-[10px] font-mono text-cyan-300 uppercase tracking-wider">
              {record ? "保存修改" : "创建记录"}
            </span>
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// 催款记录弹窗（仅应收）
// ============================================
function CollectionModal({
  record,
  onSubmit,
  onClose,
}: {
  record: AccountRecord;
  onSubmit: (collection: Omit<CollectionRecord, "id">) => void;
  onClose: () => void;
}) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState<CollectionRecord["method"]>("phone");
  const [contactPerson, setContactPerson] = useState("");
  const [result, setResult] = useState("");
  const [nextAction, setNextAction] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = () => {
    if (!contactPerson.trim()) {
      setError("请输入联系人");
      return;
    }
    if (!result.trim()) {
      setError("请输入催款结果");
      return;
    }
    onSubmit({
      date,
      method,
      contactPerson: contactPerson.trim(),
      result: result.trim(),
      nextAction: nextAction.trim() || undefined,
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md max-h-[85vh] flex flex-col rounded-xl border border-amber-500/30 bg-[#0F1623] overflow-hidden shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
              <Phone size={13} className="text-amber-400" />
            </div>
            <div>
              <h3 className="text-[13px] font-semibold text-white">添加催款记录</h3>
              <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                {record.counterparty} · 未结 {formatMoney(outstandingAmount(record))}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-white/[0.06] text-gray-400 hover:text-white transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        {/* 表单 */}
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {error && (
            <div className="px-3 py-2 rounded border border-red-500/30 bg-red-500/10 text-[10px] text-red-300 font-mono">
              {error}
            </div>
          )}

          {/* 催款日期 */}
          <div>
            <label className="block text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1.5">
              催款日期
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-3 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono outline-none focus:border-amber-500/30"
            />
          </div>

          {/* 催款方式 */}
          <div>
            <label className="block text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1.5">
              催款方式
            </label>
            <div className="grid grid-cols-4 gap-1.5">
              {(
                [
                  { key: "phone", label: "电话", icon: Phone },
                  { key: "email", label: "邮件", icon: Mail },
                  { key: "wechat", label: "微信", icon: MessageSquare },
                  { key: "visit", label: "上门", icon: Users },
                ] as const
              ).map((opt) => {
                const OptIcon = opt.icon;
                return (
                  <button
                    key={opt.key}
                    onClick={() => setMethod(opt.key)}
                    className={`flex flex-col items-center gap-1 px-2 py-2 rounded-md border transition-colors ${
                      method === opt.key
                        ? "border-amber-500/40 bg-amber-500/15 text-amber-300"
                        : "border-white/[0.06] bg-white/[0.02] text-gray-500 hover:bg-white/[0.04]"
                    }`}
                  >
                    <OptIcon size={12} />
                    <span className="text-[9px] font-mono">{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* 联系人 */}
          <div>
            <label className="block text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1.5">
              联系人
            </label>
            <input
              type="text"
              value={contactPerson}
              onChange={(e) => setContactPerson(e.target.value)}
              placeholder="对接人姓名"
              className="w-full px-3 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 placeholder-gray-600 outline-none focus:border-amber-500/30"
            />
          </div>

          {/* 催款结果 */}
          <div>
            <label className="block text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1.5">
              催款结果
            </label>
            <textarea
              value={result}
              onChange={(e) => setResult(e.target.value)}
              placeholder="对方承诺、反馈意见、争议点..."
              rows={3}
              className="w-full px-3 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 placeholder-gray-600 outline-none focus:border-amber-500/30 resize-none"
            />
          </div>

          {/* 下次行动 */}
          <div>
            <label className="block text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1.5">
              下次行动（可选）
            </label>
            <input
              type="text"
              value={nextAction}
              onChange={(e) => setNextAction(e.target.value)}
              placeholder="下次催款计划/跟进动作"
              className="w-full px-3 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 placeholder-gray-600 outline-none focus:border-amber-500/30"
            />
          </div>

          {/* 历史催款记录 */}
          {record.collectionHistory && record.collectionHistory.length > 0 && (
            <div className="rounded-md border border-white/[0.04] bg-white/[0.01] p-2">
              <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1.5">
                历史催款记录（{record.collectionHistory.length} 次）
              </div>
              <div className="space-y-1 max-h-[120px] overflow-y-auto">
                {record.collectionHistory
                  .slice()
                  .reverse()
                  .map((c) => {
                    const methodMeta = COLLECTION_METHOD_META[c.method];
                    const MethodIcon = methodMeta.icon;
                    return (
                      <div
                        key={c.id}
                        className="flex items-start gap-1.5 px-1.5 py-1 rounded bg-white/[0.02]"
                      >
                        <MethodIcon size={9} className="text-gray-500 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-[9px] font-mono text-gray-400">
                            {formatDate(c.date)} · {c.contactPerson}
                          </div>
                          <div className="text-[9px] text-gray-300">{c.result}</div>
                          {c.nextAction && (
                            <div className="text-[9px] text-amber-400/80">
                              下一步：{c.nextAction}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-white/[0.06]">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-md text-[10px] font-mono text-gray-400 hover:text-white hover:bg-white/[0.04] transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500/20 border border-amber-500/40 hover:bg-amber-500/30 transition-colors"
          >
            <Save size={11} className="text-amber-400" />
            <span className="text-[10px] font-mono text-amber-300 uppercase tracking-wider">
              保存催款记录
            </span>
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// 收/付款弹窗（应收收款 / 应付付款）
// ============================================
function PaymentModal({
  record,
  onSubmit,
  onClose,
}: {
  record: AccountRecord;
  onSubmit: (paidAmount: number) => void;
  onClose: () => void;
}) {
  const outstanding = outstandingAmount(record);
  const [paidAmount, setPaidAmount] = useState<number>(record.paidAmount);
  const [error, setError] = useState<string | null>(null);
  const isReceivable = record.type === "receivable";

  const handleSubmit = () => {
    if (paidAmount < 0) {
      setError("金额不能为负");
      return;
    }
    if (paidAmount > record.amount) {
      setError(`金额不能超过总金额 ${formatMoney(record.amount)}`);
      return;
    }
    onSubmit(paidAmount);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm max-h-[85vh] flex flex-col rounded-xl border border-green-500/30 bg-[#0F1623] overflow-hidden shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-green-500/15 border border-green-500/30 flex items-center justify-center">
              <Banknote size={13} className="text-green-400" />
            </div>
            <div>
              <h3 className="text-[13px] font-semibold text-white">
                {isReceivable ? "记录收款" : "记录付款"}
              </h3>
              <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                {record.counterparty}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-white/[0.06] text-gray-400 hover:text-white transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        {/* 表单 */}
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {error && (
            <div className="px-3 py-2 rounded border border-red-500/30 bg-red-500/10 text-[10px] text-red-300 font-mono">
              {error}
            </div>
          )}

          {/* 金额汇总 */}
          <div className="grid grid-cols-3 gap-2">
            <div className="px-2 py-1.5 rounded-md border border-white/[0.04] bg-white/[0.01]">
              <div className="text-[8px] font-mono text-gray-500 uppercase">总金额</div>
              <div className="text-[11px] font-mono text-gray-200">
                {formatMoney(record.amount)}
              </div>
            </div>
            <div className="px-2 py-1.5 rounded-md border border-white/[0.04] bg-white/[0.01]">
              <div className="text-[8px] font-mono text-gray-500 uppercase">已{isReceivable ? "收" : "付"}</div>
              <div className="text-[11px] font-mono text-green-400">
                {formatMoney(record.paidAmount)}
              </div>
            </div>
            <div className="px-2 py-1.5 rounded-md border border-amber-500/20 bg-amber-500/[0.04]">
              <div className="text-[8px] font-mono text-gray-500 uppercase">未结</div>
              <div className="text-[11px] font-mono text-amber-400">
                {formatMoney(outstanding)}
              </div>
            </div>
          </div>

          {/* 累计已收/付金额 */}
          <div>
            <label className="block text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1.5">
              累计已{isReceivable ? "收" : "付"}金额（元）
            </label>
            <div className="flex items-center gap-1">
              <span className="text-[10px] font-mono text-gray-600">¥</span>
              <input
                type="number"
                min={0}
                max={record.amount}
                step="0.01"
                value={paidAmount || ""}
                onChange={(e) =>
                  setPaidAmount(Math.max(0, Number(e.target.value) || 0))
                }
                placeholder="0"
                className="w-full px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 text-right font-mono placeholder-gray-600 outline-none focus:border-green-500/30"
              />
            </div>
            <p className="text-[8px] font-mono text-gray-600 mt-1">
              填入累计已收/付总额，结清时填入总金额即可
            </p>
          </div>

          {/* 快捷按钮 */}
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setPaidAmount(record.paidAmount)}
              className="flex-1 px-2 py-1 rounded-md text-[9px] font-mono text-gray-400 bg-white/[0.02] border border-white/[0.06] hover:bg-white/[0.04] transition-colors"
            >
              重置为原值
            </button>
            <button
              onClick={() => setPaidAmount(record.amount)}
              className="flex-1 px-2 py-1 rounded-md text-[9px] font-mono text-green-300 bg-green-500/10 border border-green-500/20 hover:bg-green-500/20 transition-colors"
            >
              全额结清
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-white/[0.06]">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-md text-[10px] font-mono text-gray-400 hover:text-white hover:bg-white/[0.04] transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-green-500/20 border border-green-500/40 hover:bg-green-500/30 transition-colors"
          >
            <Save size={11} className="text-green-400" />
            <span className="text-[10px] font-mono text-green-300 uppercase tracking-wider">
              确认{isReceivable ? "收款" : "付款"}
            </span>
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
